import { UserService } from "@/services/user.service";
import { TrainingService } from "@/services/training.service";
import { BTrainingService } from "@/services/btraining.service";
import { ExternalTrainingService } from "@/services/externalTraining.service";
import { TextPageService } from "@/services/textPage.service";
import { ContactService } from "@/services/contact.service";
import { OrderService } from "@/services/order.service";
import TokenService from "@/services/storage.service";
import { PriceService } from "@/services/price.service";
import { ProfileService } from "@/services/profile.service";
import { MessageService } from "@/services/message.service";
import { MediaService } from "@/services/media.service";
import { WholesalerService } from "@/services/wholesaler.service";
import { ProductService } from "@/services/product.service";
import { SliderService } from "@/services/slider.service";
import { AppService } from "@/services/app.service";
import { HunterService } from "@/services/hunter.service";
import { PromotionService } from "@/services/promotion.service";
import { GroupService } from "@/services/group.service";
import ApiService from "@/services/api.service";
import router from "@/router";
import { tenantConfigs } from "@/configs/tenantConfig";
import { normalizeWholesalersDealersList } from "@/utils/normalizeWholesalersDealers";
import { normalizeListFromApi } from "@/utils/normalizeListFromApi";
import { resolveProfilePercentage } from "@/utils/resolveProfilePercentage";
import store from "@/store";
import {
  clearAuthSession,
  forceLogoutToLogin,
  isSessionExpiredResponse,
  isUnauthorizedMeResponse,
} from "@/auth/session";

let meRequest = null;

function persistProfilePercentage(commit, rawPercentage) {
  const resolved = resolveProfilePercentage(rawPercentage);
  if (resolved != null) {
    commit("SET_PERCENTAGE", resolved);
    TokenService.saveProfilePercentage(resolved);
    return;
  }

  // API returned null/empty — keep a previously saved numeric value when present
  const existing =
    resolveProfilePercentage(sessionStorage.getItem("percentage")) ??
    resolveProfilePercentage(store.state.profilePercentageNum);

  if (existing != null) {
    commit("SET_PERCENTAGE", existing);
    return;
  }

  commit("SET_PERCENTAGE", 0);
  TokenService.saveProfilePercentage(0);
}

function isValidJsonString(str) {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed); // Ensure it is an array
  } catch (e) {
    return false;
  }
}

function isValidJsonObjectString(str) {
  try {
    const parsed = JSON.parse(str);
    return (
      parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
    );
  } catch (e) {
    return false;
  }
}

const actions = {
  async userB2CLogin(_, params) {
    clearAuthSession();

    if (!params.token || !params.userId) {
      await router.push({ name: "login" });
      return;
    }

    await ApiService.csrfCookie();

    TokenService.saveToken(params.token);
    TokenService.saveUserHashId(params.userId);
    ApiService.setHeader();

    await store.dispatch("me").catch(() => null);
    await store.dispatch("fetchTenantPermissions", true);

    await router.push({ name: "dashboard" });
  },

  async logout() {
    let redirectUrl = null;

    try {
      const response = await ApiService.post("users/logout");
      redirectUrl = response?.data?.url || null;
    } catch (error) {
      // Session may already be expired
    }

    clearAuthSession();

    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }

    await router.push({ name: "login" });
  },

  updateUser(_, userData) {
    return new Promise((resolve, reject) => {
      UserService.updateUserData(userData.id, userData)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error.response);
        });
    });
  },

  updateIntro(_, userData) {
    return new Promise((resolve, reject) => {
      UserService.updateUserIntro(userData.id, userData)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error.response);
        });
    });
  },

  async me({ commit, getters }) {
    if (!TokenService.getToken()) {
      forceLogoutToLogin();
      return null;
    }

    if (meRequest) {
      return meRequest;
    }

    meRequest = (async () => {
      commit("SET_ME_IS_PENDING", true);

      try {
        const response = await UserService.me();

        if (isUnauthorizedMeResponse(response)) {
          forceLogoutToLogin();
          return response;
        }

        const responseUser = response?.data?.data;
        if (!responseUser) {
          return response;
        }

        const output = {
          ...responseUser,
          wholesalers_dealers: normalizeWholesalersDealersList(
            responseUser.wholesalers_dealers,
          ),
        };

        TokenService.setUser(output);
        commit("SET_USER", output);
        commit("SET_PROFILE", output.profile);
        TokenService.saveUserHashId(output.id);

        commit("SET_LAST_BANK_STATEMENT", output.last_bank_record);
        if (getters.isLiteVersion) {
          const initialSurveyStep =
            Number(output.light_version_intro ?? output.intro ?? 0) || 0;
          commit("SET_INITIAL_SURVEY_STEP", initialSurveyStep);
        }

        persistProfilePercentage(commit, output.profile?.percentage);
        commit("SET_UNREAD_MESSAGES", output.unread_messages);
        commit("SET_ADDRESSES", output.addresses);

        return response;
      } catch (err) {
        if (isSessionExpiredResponse(err)) {
          forceLogoutToLogin();
          return null;
        }

        console.error("[Auth] Failed to load current user:", err);
        return null;
      } finally {
        commit("SET_ME_IS_PENDING", false);
        meRequest = null;
      }
    })();

    return meRequest;
  },

  async loadProfileFormData({ commit, getters }, options = {}) {
    try {
      const response = await ProfileService.getProfileFormData({
        includeWholesalers: options.includeWholesalers !== false,
      });

      if (response?.status !== 200 || !response?.data?.data) {
        return response;
      }

      const {
        user: rawUser,
        parent_attributes: parentAttributes,
        wholesalers,
        groups,
      } = response.data.data;

      const normalizeWholesalerList = (list) => {
        if (!list) return [];
        const arr = Array.isArray(list) ? list : Object.values(list);
        return arr.map((item) => ({
          ...item,
          id: item.id,
          name: item.name ?? "",
        }));
      };

      const allWholesalerRows = normalizeWholesalerList(
        wholesalers?.allWholesalers,
      );
      const filteredWholesalerRows = normalizeWholesalerList(
        wholesalers?.wholesalers,
      );
      const wholesalerNameById = new Map();
      for (const w of [...allWholesalerRows, ...filteredWholesalerRows]) {
        wholesalerNameById.set(String(w.id), w.name);
      }

      const dealersFromApi = normalizeWholesalersDealersList(
        rawUser?.wholesalers_dealers,
      );
      const dealersFromStore = normalizeWholesalersDealersList(
        getters.getMe?.wholesalers_dealers,
      );
      const apiIncludesDealersList =
        rawUser != null &&
        Object.prototype.hasOwnProperty.call(rawUser, "wholesalers_dealers");
      const dealersSource = apiIncludesDealersList
        ? dealersFromApi
        : dealersFromApi.length > 0
        ? dealersFromApi
        : dealersFromStore;

      const user = {
        ...rawUser,
        wholesalers_dealers: dealersSource.map((row) => ({
          ...row,
          wholesaler_name:
            wholesalerNameById.get(String(row?.wholesaler_id)) ??
            row?.wholesaler_name ??
            row?.expected_name ??
            null,
        })),
      };

      const profilePayload = user.profile ?? null;

      TokenService.setUser(user);
      commit("SET_USER", user);
      commit("SET_PROFILE", profilePayload);
      TokenService.saveUserHashId(user.id);
      commit("SET_LAST_BANK_STATEMENT", user.last_bank_record);

      if (getters.isLiteVersion) {
        const initialSurveyStep =
          Number(user.light_version_intro ?? user.intro ?? 0) || 0;
        commit("SET_INITIAL_SURVEY_STEP", initialSurveyStep);
      }

      persistProfilePercentage(commit, user.profile?.percentage);
      commit("SET_UNREAD_MESSAGES", user.unread_messages);
      commit("SET_ADDRESSES", user.addresses ?? []);

      if (parentAttributes) {
        commit("SET_PARENT_ATTRIBUTES", parentAttributes);
      }

      if (wholesalers) {
        commit(
          "SET_WHOLESALERS",
          normalizeWholesalerList(wholesalers.wholesalers),
        );
        commit(
          "SET_ALL_WHOLESALERS",
          normalizeWholesalerList(wholesalers.allWholesalers),
        );
      }

      commit("SET_GROUPS", groups);

      return response;
    } catch (err) {
      console.error("loadProfileFormData failed", err);
      return null;
    }
  },

  async bankStatement({ commit }, data) {
    try {
      const response = await UserService.bankStatement(data);

      commit("SET_BANK_STATEMENT", response.data);
      commit("SET_CURRENT_POINTS_KICKBACKS", response.data);

      return response.data;
    } catch (err) {
      router.push("/");
    }
  },

  async wshBankStatement({ commit }, data) {
    try {
      const response = await WholesalerService.wshBankStatement(data);

      commit("SET_BANK_STATEMENT", response.data);
      commit("SET_CURRENT_POINTS_KICKBACKS", response.data);
    } catch (err) {
      router.push("/");
    }
  },

  async loadUserAddresses({ commit, state }) {
    try {
      const response = await UserService.getUserAddresses(state.user.id);
      commit("SET_ADDRESSES", response.data);
    } catch (err) {
      console.error(err);
    }
  },

  // eslint-disable-next-line no-unused-vars
  async storeAddress(_, data) {
    try {
      const response = await OrderService.storeAddress(data);

      if (response.status === 200) {
        return true;
      }
    } catch (err) {
      router.push("/");
    }
  },

  async deleteAddress(data, id) {
    try {
      const response = await OrderService.deleteAddress(id);

      if (response.status === 200) {
        return true;
      }
    } catch (err) {
      console.error(err);
    }
  },

  async loadCategories({ commit, state }) {
    if (state.categories.length) {
      return;
    }
    if (state.categoriesRequestSent) {
      return;
    }
    try {
      commit("SET_CATEGORIES_REQUEST_SENT", true);
      const response = await ProductService.categories();
      commit("SET_CATEGORIES", response.data.data);
    } catch (err) {
      router.push("/");
    } finally {
      commit("SET_CATEGORIES_REQUEST_SENT", false);
    }
  },

  async loadTrainings({ commit }, type = "") {
    try {
      const response = await TrainingService.trainings(type);
      const trainings = response?.data?.data ?? response?.data ?? [];
      const normalizedTrainings = Array.isArray(trainings)
        ? trainings.map((training) => ({
            ...training,
            id: training.id ?? training.hashId,
            poster: training.poster ?? training.image,
          }))
        : [];

      commit("SET_TRAININGS", normalizedTrainings);
    } catch (error) {
      router.push("/");
    }
  },

  async loadPrices({ commit }) {
    try {
      const response = await PriceService.prices();
      commit("SET_PRICES", response.data.data);
    } catch (error) {
      router.push("/");
    }
  },

  async loadSinglePriceList({ commit }, slug) {
    try {
      const response = await PriceService.singlePriceList(slug);
      commit("SET_SINGLE_PRICE_LIST", response.data.data);
    } catch (error) {
      router.push("/");
    }
  },

  async loadPriceCategories({ commit }) {
    try {
      const response = await PriceService.priceCategories();
      commit("SET_PRICE_CATEGORIES", response.data.data);
    } catch (error) {
      router.push("/");
    }
  },

  async loadProducts({ dispatch, commit }, page) {
    try {
      dispatch("addPending", true);
      commit("SET_PRODUCT_REQUEST_SENT", true);
      const response = await ProductService.products(page);
      commit("SET_PRODUCTS", response.data.data);
      commit("SET_PRODUCTS_META", response.data.meta);
      commit("SET_PRODUCT_REQUEST_SENT", false);
    } catch (err) {
      router.push("/");
    }
  },

  async loadExampleProducts({ commit }, userId) {
    try {
      const response = await ProductService.exmapleProducts(userId);
      if (response.status == 200) {
        commit("SET_BEST_SOLD_PRODUCTS", response.data.mostSold);
        commit("SET_SEASONAL_PRODUCTS", response.data.seasonals);
        commit("SET_SOON_AFFORDABLE_PRODUCTS", response.data.soonAfforable);
      }
    } catch (e) {
      console.error(e);
    }
  },

  async addItem({ getters }, data) {
    try {
      const token = getters.getCart?.token;
      const product = { ...data, token };

      await OrderService.addItem(product);
    } catch (err) {
      console.log(err);
    }
  },

  async updateQuantity(_, data) {
    try {
      await OrderService.updateItemQty(data);
    } catch (err) {
      console.log(err);
    }
  },

  async loadProductById({ commit }, id) {
    try {
      const response = await ProductService.product(id);
      commit("SET_PRODUCT", response.data.data);
    } catch (err) {
      router.push("/");
    }
  },

  async loadCategoryProduct({ commit }, data) {
    try {
      commit("SET_PRODUCT_REQUEST_SENT", true);
      const response = await ProductService.categoryProducts(
        data.id,
        data.pageNumber,
        data.sort,
        data.perPage,
      );
      commit("SET_PRODUCTS", response.data.data);
      commit("SET_PRODUCTS_META", response.data.meta);
      commit("SET_PRODUCT_REQUEST_SENT", false);
    } catch (err) {
      router.push("/");
    }
  },

  setAttributeOption({ commit }, attributeOption) {
    commit("SET_ATTRIBUTE_OPTIONS", attributeOption);
  },

  setAddressSelected({ commit }, data) {
    commit("SET_ADDRESS_SELECTED", data);
  },

  async deleteItem({ dispatch }, token) {
    try {
      await OrderService.deleteCartItem(token);
      await dispatch("me");
      await dispatch("fetchUserShopCart");
    } catch (err) {
      router.push("/");
    }
  },

  async deleteItems({ dispatch }, tokens = []) {
    if (!Array.isArray(tokens) || tokens.length === 0) {
      return;
    }

    try {
      for (const token of tokens) {
        await OrderService.deleteCartItem(token);
      }
      await dispatch("me");
      await dispatch("fetchUserShopCart");
    } catch (err) {
      router.push("/");
    }
  },

  // eslint-disable-next-line no-unused-vars
  async createOrder({ commit, dispatch }, data) {
    try {
      const response = await OrderService.createOrder(data);

      if (response.status === 200) {
        commit("CLEAR_USER_CART");
        await dispatch("me");
        await dispatch("fetchUserShopCart");
      }
      return response;
    } catch (err) {
      return err.data;
    }
  },

  // eslint-disable-next-line no-unused-vars
  async storeResult(_, data) {
    try {
      const response = await TrainingService.storeResult(data);

      if (response.status === 200) {
        return true;
      }
    } catch (err) {
      router.push("/");
    }
  },

  // eslint-disable-next-line no-unused-vars
  async storeBResult(_, data) {
    try {
      const response = await BTrainingService.storeResult(data);

      if (response.status === 200) {
        return true;
      }
    } catch (err) {
      return (
        err?.response?.data || {
          message: "Request failed",
        }
      );
    }
  },

  // eslint-disable-next-line no-unused-vars
  async storeExternalTraining(_, data) {
    try {
      return await ExternalTrainingService.store(data);
    } catch (err) {
      console.log(err);
    }
  },

  // eslint-disable-next-line no-unused-vars
  async storeHunterProduct(_, data) {
    try {
      const response = await HunterService.storeHunter(data);

      if (response.status === 200) {
        return response;
      } else {
        return response;
      }
    } catch (err) {
      console.log(err);
    }
  },

  async loadHunterById({ commit }, id) {
    try {
      const response = await HunterService.hunter(id);
      commit("SET_SINGLE_HUNTER", response.data.data);
    } catch (err) {
      router.push("/");
    }
  },

  // eslint-disable-next-line no-unused-vars
  async storeWatchedVideo(_, data) {
    try {
      const response = await TrainingService.storeVideoWatched(data);

      if (response.status === 200) {
        return true;
      }
    } catch (err) {
      router.push("/");
    }
  },

  async storeProfilePercentage({ commit }, data) {
    if (!data?.user_id || !data?.profile_id) {
      console.warn("storeProfilePercentage skipped: invalid payload", data);
      return null;
    }
    try {
      await ProfileService.storeProfilePercentage(data);
      persistProfilePercentage(commit, data.percentage);
    } catch (err) {
      router.push("/");
    }
  },

  async loadWatchedVideo({ commit }, data) {
    try {
      const response = await TrainingService.fetchWatchedVideo(data);

      commit("SET_WATCHED_VIDEO", response.data.success);
    } catch (err) {
      router.push("/");
    }
  },

  clearAttributes({ state }) {
    state.attribute_options = [];
  },

  async loadTrainingById({ commit }, id) {
    const response = await TrainingService.training(id);
    commit("SET_TRAINING", response.data.data);
    commit("SET_CERTIFICATE_TEXTS", response.data.data.certificates);
    commit("SET_QUESTIONS", response.data.data.questions);
    commit("SET_TRAINING_RESULT", response.data.success);
    commit("SET_END_QUIZ_DATE", response.data.created_at);
    return response.data.data;
  },

  // eslint-disable-next-line no-unused-vars
  async generateCertificate(_) {
    try {
      const response = await TrainingService.generateCertificate();

      if (response.status === 200) {
        return true;
      }
    } catch (err) {
      router.push("/");
    }
  },

  async loadBTrainings({ commit }, type = "") {
    try {
      const response = await BTrainingService.trainings(type);
      const trainings = response?.data?.data ?? response?.data ?? [];
      const normalizedTrainings = Array.isArray(trainings)
        ? trainings.map((training) => ({
            ...training,
            id: training.id ?? training.hashId,
            poster: training.poster ?? training.image ?? {},
            title: training.title ?? {},
          }))
        : [];

      commit("SET_B_TRAININGS", normalizedTrainings);
    } catch (error) {
      router.push("/");
    }
  },

  async loadBTrainingById({ commit }, id) {
    const response = await BTrainingService.training(id);
    const responseData = response?.data ?? {};
    const { data: trainingData, ...result } = responseData;
    const data = trainingData ?? {};

    commit("SET_B_TRAINING", {
      ...data,
      id: data.id ?? data.hashId,
      poster: data.poster ?? data.image ?? {},
      title: data.title ?? {},
    });
    commit("SET_B_TRAINING_DETAILS", data.bTrainingDetails ?? []);
    commit("SET_B_TRAINING_COSTS", data.allCosts ?? []);
    commit("SET_B_TRAINING_TYPES", data.allTypes ?? []);
    commit("SET_B_TRAINING_RESULT", result);
  },

  async loadBTrainingPage({ commit }, id) {
    commit("SET_B_TRAINING_LOADING", true);
    commit("RESET_B_TRAINING");

    try {
      const response = await BTrainingService.training(id);
      const responseData = response?.data ?? {};
      const { data: trainingData, ...result } = responseData;
      const data = trainingData ?? {};

      commit("SET_B_TRAINING", {
        ...data,
        id: data.id ?? data.hashId,
        poster: data.poster ?? data.image ?? {},
        title: data.title ?? {},
      });
      commit("SET_B_TRAINING_DETAILS", data.bTrainingDetails ?? []);
      commit("SET_B_TRAINING_COSTS", data.allCosts ?? []);
      commit("SET_B_TRAINING_TYPES", data.allTypes ?? []);
      commit("SET_B_TRAINING_RESULT", result);
    } catch (error) {
      router.push("/");
    } finally {
      commit("SET_B_TRAINING_LOADING", false);
    }
  },

  async loadBTrainingDetails({ commit }, id) {
    try {
      const response = await BTrainingService.bTrainingDetails(id);
      commit("SET_B_TRAINING_DETAILS", response.data.data);
    } catch (error) {
      router.push("/");
    }
  },

  async loadBTrainingCosts({ commit }) {
    try {
      const response = await BTrainingService.bTrainingCosts();
      commit("SET_B_TRAINING_COSTS", response.data.data);
    } catch (error) {
      router.push("/");
    }
  },
  async loadBTrainingTypes({ commit }) {
    try {
      const response = await BTrainingService.bTrainingTypes();
      commit("SET_B_TRAINING_TYPES", response.data.data);
    } catch (error) {
      router.push("/");
    }
  },
  async loadUserSlides({ commit }) {
    try {
      commit("SET_SLIDER_IS_LOADING", true);
      const userId = TokenService.getUserHashId();
      const response = await SliderService.sliders(userId);

      if (!response?.data) {
        commit("SET_USER_SLIDES", []);
        commit("SET_USER_MAIN_BOXES", []);
        commit("SET_TEXT_PAGES", []);
        commit("SET_MENU", []);
        commit("SET_SLIDER_IS_LOADING", false);
        return;
      }

      commit("SET_USER_SLIDES", response.data.sliders ?? []);
      commit("SET_USER_MAIN_BOXES", response.data.mainBoxes ?? []);
      commit("SET_TEXT_PAGES", response.data.textPages ?? []);
      commit("SET_MENU", response.data.menu ?? []);
      commit("SET_SLIDER_IS_LOADING", false);
    } catch (err) {
      router.push("/");
    }
  },

  async loadDealerApps({ commit }) {
    commit("SET_DEALER_APPS_PEDNING", true);
    try {
      const response = await AppService.list();
      commit("SET_DEALER_APPS", normalizeListFromApi(response));
    } catch (err) {
      console.error("Failed to load dealer apps:", err);
      commit("SET_DEALER_APPS", []);
    } finally {
      commit("SET_DEALER_APPS_PEDNING", false);
    }
  },

  async loadUserTextPageById({ commit }, id) {
    const response = await TextPageService.textPage(id);
    commit("SET_TEXT_PAGE", response.data);
  },

  async loadMessages({ commit }) {
    commit("SET_MESSAGES_IS_LOADING", true);
    try {
      const listResponse = await MessageService.loadMessages();
      const list = Array.isArray(listResponse?.data) ? listResponse.data : [];
      commit("SET_MESSAGES", list);
    } finally {
      commit("SET_MESSAGES_IS_LOADING", false);
    }
  },

  async loadMessageDetail({ commit }, id) {
    commit("SET_MESSAGE_DETAIL_LOADING", true);
    commit("SET_MESSAGE_DETAIL", null);
    try {
      const response = await MessageService.loadMessageDetail(id);
      const detail = response.data;
      commit("SET_MESSAGE_DETAIL", detail);
      commit("SET_UNREAD_MESSAGES", detail.unread_messages);
    } finally {
      commit("SET_MESSAGE_DETAIL_LOADING", false);
    }
  },

  async deleteMessages({ dispatch }, ids) {
    await MessageService.deleteMessages(ids);
    await dispatch("loadMessages");
    await dispatch("me");
  },

  // eslint-disable-next-line no-unused-vars
  addPending({ commit }, data) {
    commit("SET_PENDING", data);
  },

  // eslint-disable-next-line no-unused-vars
  addToast({ commit }, data) {
    commit("SET_TOAST", data);
  },

  // eslint-disable-next-line no-unused-vars
  async loadUserOrders({ commit }, data) {
    const response = await OrderService.orders(data);
    console.log(response);
    commit("SET_USER_ORDERS", response.data);

    let products = [];

    if (response.data.length > 0) {
      for (const element of response.data) {
        for (const item of element.items) {
          products.push({
            id: item.product_id,
            name: item.product.name,
          });
        }
      }

      commit("SET_USER_PRODUCTS", products);
    }
  },

  // eslint-disable-next-line no-unused-vars
  async loadUserOrderProducts({ commit }) {
    const response = await OrderService.orderProducts();
    commit("SET_USER_PRODUCTS", response.data);
  },

  async loadUserPromotions({ commit }) {
    try {
      const response = await UserService.userPromotions();

      if (response.status === 200) {
        const promotionsPayload = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];

        commit("SET_USER_PROMOTIONS", promotionsPayload);
      }
    } catch (err) {
      console.log(err);
    }
  },
  async loadNewsFeeds({ commit }, page = 1) {
    const response = await TextPageService.newsFeeds(page);
    commit("SET_NEWS_FEEDS", response.data.data);
    commit("SET_NEWS_FEEDS_META", response.data.meta);
  },

  addCurrentNewsFeed({ commit }, index) {
    commit("SET_CURRENT_NEWS_FEED", index);
  },

  async loadWholeSalers({ commit }) {
    const response = await WholesalerService.wholesalers();
    const normalizeWholesalerList = (list) => {
      if (!list) return [];
      const arr = Array.isArray(list) ? list : Object.values(list);
      return arr.map((item) => ({
        ...item,
        id: item.id,
        name: item.name ?? "",
      }));
    };

    commit("SET_WHOLESALERS", normalizeWholesalerList(response.wholesalers));
    commit(
      "SET_ALL_WHOLESALERS",
      normalizeWholesalerList(response.allWholesalers),
    );
  },

  async loadGroups({ commit }) {
    const response = await UserService.groups();
    if (response?.data != null) {
      commit("SET_GROUPS", response.data);
    }
  },

  async loadKickbackInfo({ commit }, groupId) {
    const response = await ProfileService.showKickbackInfo(groupId);
    if (response?.status === 200 && response.data != null) {
      commit("SET_KICKBACK_INFO", response.data);
    }
  },

  async loadPointsByCsNumber({ commit }, data) {
    const response = await ProfileService.showPointsByCustomerNumber(data);
    commit("SET_POINTS_BY_CUSTOMER_NUMBER", response.data);
  },

  async loadMenu({ commit }) {
    try {
      const response = await UserService.getMenu();

      if (response.status == 200) {
        commit("SET_MENU", response.data.data);
      }
    } catch (e) {
      console.error(e);
    }
  },

  async fetchMediaCategories({ commit }) {
    try {
      const response = await MediaService.mediaCategories();

      if (response.status == 200) {
        commit("SET_MEDIA_CATEGORIES", response.data.data);

        return response.data.data;
      }
    } catch (e) {
      console.error(e);
    }
  },

  async fetchSingleMediaList({ commit }, data) {
    try {
      const response = await MediaService.singleMedia(data);

      if (response.status == 200) {
        commit("SET_SINGLE_MEDIA", response.data);
      }
    } catch (e) {
      console.error(e);
    }
  },

  async fetchSingleMediaCategory({ commit }, id) {
    try {
      const response = await MediaService.singleMediaCategory(id);

      if (response.status == 200) {
        commit("SET_SINGLE_MEDIA_CATEGORY", response.data.data);
      }
    } catch (e) {
      console.error(e);
    }
  },

  addAllDealersNumber({ commit }, data) {
    commit("SET_ALL_DEALERS_NUMBER", data);
  },

  async fetchWshSelect({ commit, getters }) {
    try {
      const currentWshSelect = getters.getWshSelect;

      if (Array.isArray(currentWshSelect) && currentWshSelect.length > 0) {
        return currentWshSelect;
      }

      const response = await WholesalerService.wholesalerSelect();

      if (response.status == 200) {
        const wshSelect = Array.isArray(response.data) ? response.data : [];
        commit("SET_WSH_SELECT", wshSelect);
        return wshSelect;
      }

      return [];
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  async fetchDefaultBanners({ commit }) {
    try {
      const response = await SliderService.banners();

      if (response.status == 200) {
        commit("SET_DEFAULT_BANNERS", response.data);
      }
    } catch (e) {
      console.error(e);
    }
  },

  checkUserPermission({ state }, name) {
    let data = sessionStorage.getItem("permissions")
      ? sessionStorage.getItem("permissions")
      : state.permissions;

    if (data.length > 0) {
      const search = data.search(name);
      return search != -1;
    }
    return false;
  },

  async filterMediaFiles({ commit }, data) {
    try {
      const response = await MediaService.fetchFilteredMedia(data);

      if (response.status == 200) {
        commit("SET_MEDIA_FILES", response.data.data);
        commit("SET_PAGINATION_LINKS", response.data.links);
        commit("SET_TOTAL_MEDIA_FILES", response.data.total);
      }
    } catch (e) {
      console.error(e);
    }
  },

  async filterBrochureFiles({ commit }, data) {
    try {
      const response = await PriceService.fetchFilteredBrochure(data);

      if (response.status == 200) {
        commit("SET_BROCHURE_FILES", response.data.data);
        commit("SET_PAGINATION_LINKS", response.data.meta.links);
        commit("SET_TOTAL_MEDIA_FILES", response.data.meta.total);
      }
    } catch (e) {
      console.error(e);
    }
  },

  async fetchTyreLimit({ commit }, payload) {
    try {
      const year =
        typeof payload === "object" &&
        payload !== null &&
        payload.year !== undefined
          ? payload.year
          : payload;
      const force =
        typeof payload === "object" &&
        payload !== null &&
        Boolean(payload.force);

      if (force) {
        TokenService.removeTyreStatus();
      }

      const tyreStatus = TokenService.getTyreStatus();

      if (!tyreStatus) {
        const response = await ProfileService.tyreLimitYearly(year);

        if (response?.status == 200) {
          TokenService.saveTyreStatus(JSON.stringify(response.data));
          commit("SET_TYRE_LIMIT", response.data.limit);
          commit("SET_BONUS_MULTIPILICATOR", response.data.bonusGoals);
          commit("SET_POINT_BONUS_TYRES_BY_YEAR", response.data.output);
        }
      } else {
        const parseedData = JSON.parse(tyreStatus);
        commit("SET_TYRE_LIMIT", parseedData.limit);
        commit("SET_BONUS_MULTIPILICATOR", parseedData.bonusGoals);
        commit("SET_POINT_BONUS_TYRES_BY_YEAR", parseedData.output);
      }
    } catch (e) {
      console.error(e);
    }
  },

  async fetchPromotions({ commit }) {
    try {
      const response = await PromotionService.promotions();

      if (response.status == 200) {
        const mapData = response.data.data.map((item) => {
          return {
            title: item.title ?? {},
            subtitle: item.subtitle ?? {},
            description: item.description ?? {},
            link: item.file ?? {},
            image: item.image ?? {},
            slug: item.slug,
            plainId: item.plainId,
            id: item.plainId,
          };
        });
        commit("SET_PROMOTIONS", mapData);
      }
    } catch (e) {
      console.error(e);
    }
  },

  async fetchBrochureCategories({ commit }) {
    try {
      const response = await PriceService.brochureCategories();

      if (response.status == 200) {
        commit("SET_BROCHURE_CATEGORIES", response.data.data);

        return response.data.data;
      }
    } catch (e) {
      console.error(e);
    }
  },

  async fetchUserShopCart({ commit, getters }) {
    const userId = TokenService.getUserId() || getters.getMe?.plainId;
    if (!userId) {
      return null;
    }

    const response = await OrderService.fetchUserOpenCart(userId);

    if (response?.status === 200) {
      if (response?.data?.data) {
        commit("SET_USER_CART", response.data.data);

        if (!TokenService.getUserId() && getters.getMe?.plainId) {
          TokenService.saveUserId(getters.getMe.plainId);
        }

        return response.data.data;
      }

      commit("CLEAR_USER_CART");
      return null;
    }

    if (response?.response?.status === 404) {
      commit("CLEAR_USER_CART");
    }

    return null;
  },

  checkMenuFetched({ commit }) {
    commit("SET_FETCH_MENU", true);
  },
  checkLeftColumnFetched({ commit }) {
    commit("SET_FETCH_LEFT_COLUMN", true);
  },
  displayInitialSurvey({ commit }, step) {
    commit("SET_INITIAL_SURVEY_STEP", step);
  },

  async loadParentAttributes({ commit }, id) {
    try {
      const response = await GroupService.parentAttributes(id);
      commit("SET_PARENT_ATTRIBUTES", response.data);
    } catch (err) {
      return err;
    }
  },

  addInitialParentAttributes({ commit }, data) {
    commit("SET_INITIAL_PARENT_ATTRIBUTES", data);
  },

  async loadBussinessUnits({ commit }) {
    const response = await GroupService.businessUnits();
    if (response.status === 200) {
      const output = [];
      for (const element of response.data) {
        for (const child of element.children) {
          output.push(child);
        }
      }

      commit("SET_BUSSINESS_UNITS", output);
    }
  },
  async fetchTenantId({ getters }) {
    if (getters.tenantId) {
      return getters.tenantId; // Return cached tenantId if available
    }
  },
  async loadTenantConfig({ commit }, countryCode) {
    const countryConfig =
      tenantConfigs.tenants[countryCode] ?? tenantConfigs.tenants["DEFAULT"];
    // Validate if the country code exists in the map
    if (!countryConfig) {
      throw new Error(`Config for ${countryCode} is not defined. Halted.`);
    }
    commit("SET_IS_LITE_VERSION", countryConfig.liteVersion);
    commit("SET_AVAILABLE_MODULES", countryConfig.modules);
    commit("SET_TENANCY_ENABLED", true);
    commit("SET_TENANT_ID", countryCode);
  },

  async loadAboutPage({ commit }) {
    const response = await TextPageService.aboutPage();
    const sorted = response.data.sort((a, b) => a.order - b.order);
    commit("SET_ABOUT_PAGE", sorted);
  },
  async loadContacts({ commit }) {
    const response = await ContactService.contactServices();
    commit("SET_CONTACT_SERVICE", response.data);
  },
  async fetchTenantPermissions({ commit }, forceFetch = true) {
    try {
      let tenantPermissions = localStorage.getItem("tenantPermissions");
      let tenantModuleConfigs = localStorage.getItem("tenantModuleConfigs");

      if (
        !isValidJsonString(tenantPermissions) ||
        !isValidJsonObjectString(tenantModuleConfigs) ||
        forceFetch
      ) {
        const response = await ProfileService.permissions();
        tenantPermissions = response.data.permissions;
        tenantModuleConfigs = response.data.moduleConfigs ?? {};
        commit(
          "SET_ACCESS_RESTRICTED",
          response.data.access._restricted_access,
        );
        commit("SET_ACCESS_DATA", response.data.access);

        TokenService.savePermissions(response.data.oldPermissions);
        commit("SET_PERMISSIONS", response.data.oldPermissions);

        if (!tenantPermissions || !Array.isArray(tenantPermissions)) {
          throw new Error("Invalid tenantPermissions data from server");
        }

        localStorage.setItem(
          "tenantPermissions",
          JSON.stringify(tenantPermissions),
        );
        localStorage.setItem(
          "tenantModuleConfigs",
          JSON.stringify(tenantModuleConfigs),
        );
      } else {
        tenantPermissions = JSON.parse(tenantPermissions);
        tenantModuleConfigs = JSON.parse(tenantModuleConfigs);
      }

      commit("SET_TENANT_PERMISSIONS", tenantPermissions);
      commit("SET_TENANT_MODULE_CONFIGS", tenantModuleConfigs);
    } catch (error) {
      console.error("Error fetching tenantPermissions:", error);
      commit("SET_TENANT_PERMISSIONS", []);
      commit("SET_TENANT_MODULE_CONFIGS", {});
      commit("SET_PERMISSIONS", []);
    }
  },

  async fetchPointBoard({ commit }) {
    const response = await ProfileService.pointBoard();
    commit("SET_POINT_BOARD", response.data);
  },
};

export { actions };
